import { Zap, StopCircle, Pause } from "lucide-react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Progress } from "@/app/_module/components/ui/progress";

interface PowerDialerBannerProps {
  currentIndex: number;
  totalLeads: number;
  isPaused: boolean;
  successCount: number;
  dispositionCount: number;
  failedCount: number;
  skippedCount: number;
  onPause: () => void;
  onStop: () => void;
  isRetryAttempt?: boolean;
}

export const PowerDialerBanner = function({
  currentIndex,
  totalLeads,
  isPaused,
  successCount,
  dispositionCount,
  failedCount,
  skippedCount,
  onPause,
  onStop,
  isRetryAttempt,
}: PowerDialerBannerProps) {
  const progress = (currentIndex / totalLeads) * 100;

  return (
    <Card className="border-2 border-orange-200 bg-orange-50/50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-orange-600" />
              <span className="font-semibold text-lg">PowerDial Session</span>
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
              {!!isPaused && <Badge variant="secondary">Paused</Badge>}
            </div>
            <div className="text-sm text-gray-600">
              {isRetryAttempt ? (
                <>Retrying Lead {currentIndex + 1} of {totalLeads} • {Math.round(progress)}% Complete</>
              ) : (
                <>Lead {currentIndex + 1} of {totalLeads} • {Math.round(progress)}% Complete</>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              className="flex items-center gap-2"
              onClick={onPause}
              size="sm"
              variant="outline"
            >
              <Pause className="h-4 w-4" />
              {isPaused ? "Resume" : "Pause"}
            </Button>
            <Button onClick={onStop} size="sm" variant="destructive">
              <StopCircle className="h-4 w-4 mr-2" />
              End Session
            </Button>
          </div>
        </div>

        <div className="mt-3">
          <Progress className="h-2" value={progress} />
        </div>

        <div className="mt-3 grid grid-cols-4 gap-3 text-center">
          <div className="bg-white rounded-lg p-2 border">
            <div className="text-lg font-bold text-green-600">
              {successCount}
            </div>
            <div className="text-xs text-muted-foreground">Completed</div>
          </div>
          <div className="bg-white rounded-lg p-2 border">
            <div className="text-lg font-bold text-blue-600">
              {dispositionCount}
            </div>
            <div className="text-xs text-muted-foreground">Dispositioned</div>
          </div>
          <div className="bg-white rounded-lg p-2 border">
            <div className="text-lg font-bold text-red-600">{failedCount}</div>
            <div className="text-xs text-muted-foreground">Bad #/DNC</div>
          </div>
          <div className="bg-white rounded-lg p-2 border">
            <div className="text-lg font-bold text-gray-600">
              {skippedCount}
            </div>
            <div className="text-xs text-muted-foreground">Skipped</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
